import React, { useCallback, useContext, useMemo } from "react";
import { HeaderA } from "components/headers";
import lang from "translations";
import { Path } from "paths";
import { VenueContext } from "contexts";
import { useApi, useMount, useFilter } from "hooks";
import { searchReservations } from "apis/reservation.api";
import { ButtonLink, DataTable, Pill, Text } from "components/commons";
import { PillType } from "enums";
import { mapObject } from "services";
import { reservationListResponse, reservationListFilterRequest } from "mappers/reservation.mapper";
import { ModuleWrapper } from "components/fragments";
import { reservationListFilterState } from "./filters";
import ReservationFilter from "./reservation-filter";
import columns from "./columns";

const lifecycleStatusPillType = (status) => {
  switch ((status || "").toUpperCase()) {
    case "CLOSED":
    case "PAID":
    case "COMPLETED":
      return PillType.Green;
    case "VOIDED":
    case "CANCELLED":
      return PillType.Red;
    default:
      return PillType.Yellow;
  }
};

const renderDateTime = (dateTime) => (
  <div className="w-20 max-w-20 md:w-32.5 md:max-w-32.5">
    <Text>{dateTime?.date}</Text>
    {dateTime?.time && <Text color="text-gray">{dateTime.time}</Text>}
  </div>
);

const ReservationList = () => {
  const { venue } = useContext(VenueContext);

  const {
    request,
    loading = true,
    error,
    result = { data: [], metadata: { total: 0 } },
    mappedData,
  } = useApi({
    api: searchReservations,
    isArray: true,
    mapper: reservationListResponse,
  });

  const { modifyFilter, modifyFilters, clearFilter, filterState, requestState } = useFilter(
    reservationListFilterState(venue),
    reservationListFilterState(venue)
  );

  const fetchReservations = useCallback(
    (nextRequestState) => {
      request(mapObject(nextRequestState, reservationListFilterRequest));
    },
    [request]
  );

  useMount(() => {
    fetchReservations(requestState);
  });

  const applyFilterCb = useCallback(
    (searchKey) => {
      const { requestState: nextRequestState } = modifyFilters({ page: 1, searchKey });
      fetchReservations(nextRequestState);
    },
    [fetchReservations, modifyFilters]
  );

  const clearFilterCb = useCallback(() => {
    const { requestState: nextRequestState } = clearFilter();
    fetchReservations(nextRequestState);
  }, [clearFilter, fetchReservations]);

  const renderData = useMemo(() => {
    return mappedData.map(
      ({
        posReservationId,
        status,
        primaryGuestName,
        guestCount,
        resourceName,
        openedAt,
        closedAt,
        orderCount,
        billCount,
        totalPaidAmount,
      }) => {
        return {
          reservationId: (
            <ButtonLink path={Path.RESERVATION_ID(posReservationId)}>#{posReservationId}</ButtonLink>
          ),
          status: (
            <Pill size="text-xs" type={lifecycleStatusPillType(status)}>{status || "-"}</Pill>
          ),
          primaryGuestName: primaryGuestName || "-",
          guestCount: guestCount ?? "-",
          resourceName,
          openedAt: renderDateTime(openedAt),
          closedAt: renderDateTime(closedAt),
          orderCount: orderCount ?? 0,
          billCount: billCount ?? 0,
          totalPaidAmount,
        };
      }
    );
  }, [mappedData]);

  return (
    <ModuleWrapper>
      <HeaderA title={lang.reservations} description={lang.reservationsDescription} className="mb-md" />
      <ReservationFilter
        filterState={filterState}
        modifyFilter={modifyFilter}
        applyFilter={applyFilterCb}
        clearFilter={clearFilterCb}
      />
      <DataTable
        page={filterState.page}
        pageSize={filterState.pageSize}
        onChangePage={modifyFilters}
        fetchList={fetchReservations}
        total={result.metadata?.total || 0}
        loading={loading}
        columns={columns}
        data={renderData}
        error={error}
      />
    </ModuleWrapper>
  );
};

export default ReservationList;
